import React, { useEffect, useMemo, useState } from 'react';
import { USER_ROLES, ROLE_LABELS, ROLE_DESCRIPTIONS, type UserRole } from '../../systems/roles';
import { apiFetch } from '../../systems/api-client';
import { useWorkspace } from '../../systems/workspace-context';
import DepartmentLabelsEditor from './DepartmentLabelsEditor';
import ScormPackagesPanel from './ScormPackagesPanel';
import styles from './darwinx-dashboard.module.css';

type AccountType = 'sso' | 'lms' | 'inscription';

const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  sso: 'SSO',
  lms: 'LMS (SCORM)',
  inscription: 'Inscription',
};

const USERS_PAGE_SIZE = 20;
const NO_WORKSPACE_FILTER = '__none__';

interface UserFilters {
  email: string;
  accountType: '' | AccountType;
  workspaceId: string;
  role: string;
  status: '' | 'active' | 'pending';
}

const EMPTY_USER_FILTERS: UserFilters = {
  email: '',
  accountType: '',
  workspaceId: '',
  role: '',
  status: '',
};

interface Enterprise {
  id: string;
  name: string;
  domain: string;
  admin_code: string;
  user_count: number;
  user_limit: number | null;
  leaderboard_enabled?: number | boolean | string | null;
}

function isLeaderboardOn(enterprise: Enterprise): boolean {
  const flag = enterprise.leaderboard_enabled;
  return flag === 1 || flag === true || flag === '1';
}

interface WhitelistedEmail {
  email: string;
  created_by: string | null;
}

interface AdminUser {
  id: string;
  email: string;
  role: string;
  active: number;
  enterprise_id: string | null;
  account_type?: AccountType;
  created_at?: string | null;
}

interface StorageScenario {
  id: string;
  title: string;
  version?: string;
  workspaceId: string | null;
  workspaceName: string | null;
}

interface EnterpriseHost {
  host: string;
  enterprise_id: string;
  enterprise_name?: string;
}

function formatStorageScenarioLabel(scenario: StorageScenario): string {
  const titleWithVersion = scenario.version ? `${scenario.title} (${scenario.version})` : scenario.title;
  return `${titleWithVersion} — ${scenario.id}`;
}

const DarwinXDashboard: React.FC = () => {
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [storageScenarios, setStorageScenarios] = useState<StorageScenario[]>([]);
  const [whitelistedEmails, setWhitelistedEmails] = useState<WhitelistedEmail[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [savingRoleUserId, setSavingRoleUserId] = useState<string | null>(null);
  const [savingWorkspaceUserId, setSavingWorkspaceUserId] = useState<string | null>(null);
  const [selectedStoryWorkspaceId, setSelectedStoryWorkspaceId] = useState('');
  const [selectedOrphanScenarioId, setSelectedOrphanScenarioId] = useState('');
  const [linkingStory, setLinkingStory] = useState(false);
  const [unlinkingScenarioId, setUnlinkingScenarioId] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDomain, setNewWorkspaceDomain] = useState('');
  const [newWorkspaceCode, setNewWorkspaceCode] = useState('');
  const [enterpriseHosts, setEnterpriseHosts] = useState<EnterpriseHost[]>([]);
  const [newHostValue, setNewHostValue] = useState('');
  const [newHostEnterpriseId, setNewHostEnterpriseId] = useState('');
  const [oidcWorkspaceId, setOidcWorkspaceId] = useState('');
  const [oidcIssuer, setOidcIssuer] = useState('');
  const [oidcClientId, setOidcClientId] = useState('');
  const [oidcSecret, setOidcSecret] = useState('');
  const [oidcScope, setOidcScope] = useState('openid email');
  const [oidcEnabled, setOidcEnabled] = useState(true);
  const [oidcHasSecret, setOidcHasSecret] = useState(false);
  const [oidcRedirectUris, setOidcRedirectUris] = useState<string[]>([]);
  const [oidcConfigured, setOidcConfigured] = useState(false);
  const [savingOidc, setSavingOidc] = useState(false);
  const [userLimitDrafts, setUserLimitDrafts] = useState<Record<string, string>>({});
  const [savingUserLimitId, setSavingUserLimitId] = useState<string | null>(null);
  const [savingLeaderboardId, setSavingLeaderboardId] = useState<string | null>(null);
  const { refreshWorkspaces } = useWorkspace();
  const [userFilters, setUserFilters] = useState<UserFilters>(EMPTY_USER_FILTERS);
  const [userPage, setUserPage] = useState(1);
  const [labelsWorkspaceId, setLabelsWorkspaceId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnterprises();
    fetchStorageScenarios();
    fetchWhitelistedEmails();
    fetchUsers();
    fetchEnterpriseHosts();
  }, []);

  useEffect(() => {
    fetchOidcConfig(oidcWorkspaceId);
  }, [oidcWorkspaceId, enterpriseHosts]);

  const fetchStorageScenarios = async () => {
    try {
      const response = await apiFetch('/api/auth/admin/storage-scenarios');
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        setStorageScenarios(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const orphanScenarios = storageScenarios.filter((scenario) => !scenario.workspaceId);
  const linkedScenariosForWorkspace = selectedStoryWorkspaceId
    ? storageScenarios.filter((scenario) => scenario.workspaceId === selectedStoryWorkspaceId)
    : [];

  const handleLinkStory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStoryWorkspaceId || !selectedOrphanScenarioId) return;

    setLinkingStory(true);
    try {
      const response = await apiFetch(
        `/api/auth/admin/enterprises/${selectedStoryWorkspaceId}/stories`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ scenarioId: selectedOrphanScenarioId }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        setSelectedOrphanScenarioId('');
        fetchStorageScenarios();
      } else if (response.status === 409) {
        alert(
          `Cette histoire est déjà liée au workspace « ${data.workspaceName || data.workspaceId} ».`
        );
        fetchStorageScenarios();
      } else {
        alert(data.error || 'Échec du rattachement');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors du rattachement');
    } finally {
      setLinkingStory(false);
    }
  };

  const handleUnlinkStory = async (scenarioId: string, title: string) => {
    if (!selectedStoryWorkspaceId) return;
    if (
      !window.confirm(
        `Retirer « ${title} » du workspace ? Les fichiers restent sur le disque / Spaces.`
      )
    ) {
      return;
    }

    setUnlinkingScenarioId(scenarioId);
    try {
      const response = await apiFetch(
        `/api/auth/admin/enterprises/${selectedStoryWorkspaceId}/stories/${encodeURIComponent(scenarioId)}`,
        {
          method: 'DELETE',
        }
      );
      const data = await response.json();
      if (response.ok) {
        fetchStorageScenarios();
      } else {
        alert(data.error || 'Échec du retrait');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors du retrait');
    } finally {
      setUnlinkingScenarioId(null);
    }
  };

  const fetchWhitelistedEmails = async () => {
    try {
      const response = await apiFetch('/api/auth/whitelist-emails', {
      });
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        setWhitelistedEmails(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddWhitelistEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      const response = await apiFetch('/api/auth/whitelist-emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newEmail.trim() }),
      });
      if (response.ok) {
        setNewEmail('');
        fetchWhitelistedEmails();
        fetchUsers();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveWhitelistEmail = async (email: string) => {
    if (!window.confirm(`Retirer ${email} de la whitelist ?`)) return;
    try {
      const response = await apiFetch(`/api/auth/whitelist-emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchWhitelistedEmails();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiFetch('/api/auth/admin/users', {
      });
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        setUsers(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      const response = await apiFetch(`/api/auth/admin/users/${userId}/activate`, {
        method: 'POST',
      });
      if (response.ok) fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    try {
      const response = await apiFetch(`/api/auth/admin/users/${userId}/deactivate`, {
        method: 'POST',
      });
      if (response.ok) fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRoleChange = async (userId: string, role: UserRole) => {
    setSavingRoleUserId(userId);
    try {
      const response = await apiFetch(`/api/auth/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role }),
      });
      const data = await response.json();
      if (response.ok) {
        setUsers((prev) =>
          prev.map((user) => (user.id === userId ? { ...user, role: data.user.role } : user))
        );
      } else {
        alert(data.error || 'Échec de la mise à jour du rôle');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la mise à jour du rôle');
    } finally {
      setSavingRoleUserId(null);
    }
  };

  const handleWorkspaceChange = async (userId: string, enterpriseId: string) => {
    setSavingWorkspaceUserId(userId);
    try {
      const response = await apiFetch(`/api/auth/admin/users/${userId}/workspace`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enterprise_id: enterpriseId }),
      });
      const data = await response.json();
      if (response.ok) {
        setUsers((prev) =>
          prev.map((user) =>
            user.id === userId ? { ...user, enterprise_id: data.user.enterprise_id } : user
          )
        );
        fetchEnterprises();
      } else {
        alert(data.error || 'Échec de la mise à jour du workspace');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la mise à jour du workspace');
    } finally {
      setSavingWorkspaceUserId(null);
    }
  };

  const fetchEnterprises = async () => {
    try {
      const response = await apiFetch('/api/auth/admin/enterprises', {
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch enterprises');
      }

      if (Array.isArray(data)) {
        setEnterprises(data);
        setUserLimitDrafts(
          Object.fromEntries(
            data.map((enterprise: Enterprise) => [
              enterprise.id,
              enterprise.user_limit == null ? '' : String(enterprise.user_limit),
            ])
          )
        );
      } else {
        setEnterprises([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnterpriseHosts = async () => {
    try {
      const response = await apiFetch('/api/auth/admin/enterprise-hosts', {
      });
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        setEnterpriseHosts(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddEnterpriseHost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newHostValue.trim() || !newHostEnterpriseId) return;

    try {
      const response = await apiFetch('/api/auth/admin/enterprise-hosts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          host: newHostValue.trim(),
          enterprise_id: newHostEnterpriseId,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setNewHostValue('');
        setEnterpriseHosts(Array.isArray(data.hosts) ? data.hosts : []);
      } else {
        alert(data.error || 'Échec de l’ajout du mapping hôte');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l’ajout du mapping hôte');
    }
  };

  const handleDeleteEnterpriseHost = async (host: string) => {
    if (!window.confirm(`Supprimer le mapping hôte « ${host} » ?`)) return;

    try {
      const response = await apiFetch(
        `/api/auth/admin/enterprise-hosts/${encodeURIComponent(host)}`,
        {
          method: 'DELETE',
        }
      );
      const data = await response.json();
      if (response.ok) {
        fetchEnterpriseHosts();
      } else {
        alert(data.error || 'Échec de la suppression');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la suppression');
    }
  };

  const resetOidcForm = () => {
    setOidcIssuer('');
    setOidcClientId('');
    setOidcSecret('');
    setOidcScope('openid email');
    setOidcEnabled(true);
    setOidcHasSecret(false);
    setOidcRedirectUris([]);
    setOidcConfigured(false);
  };

  const fetchOidcConfig = async (enterpriseId: string) => {
    if (!enterpriseId) {
      resetOidcForm();
      return;
    }
    try {
      const response = await apiFetch(`/api/auth/admin/enterprises/${enterpriseId}/oidc`, {
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch OIDC config');
      }
      if (!data) {
        resetOidcForm();
        const hosts = enterpriseHosts
          .filter((entry) => entry.enterprise_id === enterpriseId)
          .map((entry) => entry.host);
        setOidcRedirectUris(
          hosts.map((host) => {
            const isLocal = host.includes('localhost') || host.startsWith('127.');
            const proto = isLocal ? 'http' : 'https';
            const hostname = host.includes('.') || isLocal ? host : `${host}.cybernovel.net`;
            return `${proto}://${hostname}/api/auth/oidc/callback`;
          })
        );
        return;
      }
      setOidcIssuer(data.issuer || '');
      setOidcClientId(data.client_id || '');
      setOidcSecret('');
      setOidcScope(data.scope || 'openid email');
      setOidcEnabled(Boolean(data.enabled));
      setOidcHasSecret(Boolean(data.has_client_secret));
      setOidcRedirectUris(Array.isArray(data.redirect_uris) ? data.redirect_uris : []);
      setOidcConfigured(true);
    } catch (err) {
      console.error(err);
      resetOidcForm();
    }
  };

  const handleSaveOidc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oidcWorkspaceId) return;
    setSavingOidc(true);
    try {
      const response = await apiFetch(`/api/auth/admin/enterprises/${oidcWorkspaceId}/oidc`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          issuer: oidcIssuer.trim(),
          client_id: oidcClientId.trim(),
          client_secret: oidcSecret.trim() || undefined,
          scope: oidcScope.trim() || 'openid email',
          enabled: oidcEnabled,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setOidcSecret('');
        const saved = data.oidc;
        setOidcConfigured(true);
        setOidcHasSecret(Boolean(saved?.has_client_secret));
        setOidcRedirectUris(Array.isArray(saved?.redirect_uris) ? saved.redirect_uris : []);
      } else {
        alert(data.error || 'Échec de l’enregistrement de l’IdP');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l’enregistrement de l’IdP');
    } finally {
      setSavingOidc(false);
    }
  };

  const handleDeleteOidc = async () => {
    if (!oidcWorkspaceId || !oidcConfigured) return;
    if (!window.confirm('Supprimer la configuration IdP de ce workspace ?')) return;
    try {
      const response = await apiFetch(`/api/auth/admin/enterprises/${oidcWorkspaceId}/oidc`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (response.ok) {
        resetOidcForm();
      } else {
        alert(data.error || 'Échec de la suppression');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la suppression');
    }
  };

  const formatUserLimitLabel = (enterprise: Enterprise): string => {
    if (enterprise.user_limit == null) {
      return `${enterprise.user_count} / ∞`;
    }
    return `${enterprise.user_count} / ${enterprise.user_limit}`;
  };

  const handleSaveUserLimit = async (enterpriseId: string) => {
    setSavingUserLimitId(enterpriseId);
    const raw = userLimitDrafts[enterpriseId] ?? '';
    const payload = raw.trim() === '' ? { user_limit: null } : { user_limit: Number(raw) };

    try {
      const response = await apiFetch(`/api/auth/admin/enterprises/${enterpriseId}/user-limit`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        fetchEnterprises();
      } else {
        alert(data.error || 'Échec de la mise à jour de la limite');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la mise à jour de la limite');
    } finally {
      setSavingUserLimitId(null);
    }
  };

  const handleToggleLeaderboard = async (enterpriseId: string, enabled: boolean) => {
    setSavingLeaderboardId(enterpriseId);
    try {
      const response = await apiFetch(`/api/auth/admin/enterprises/${enterpriseId}/leaderboard`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchEnterprises();
        void refreshWorkspaces();
      } else {
        alert(data.error || 'Échec de la mise à jour du classement');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la mise à jour du classement');
    } finally {
      setSavingLeaderboardId(null);
    }
  };

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWorkspaceName.trim() || !newWorkspaceDomain.trim() || !newWorkspaceCode.trim()) {
      return;
    }
    try {
      const response = await apiFetch('/api/auth/admin/enterprises', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newWorkspaceName.trim(),
          domain: newWorkspaceDomain.trim(),
          admin_code: newWorkspaceCode.trim(),
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setNewWorkspaceName('');
        setNewWorkspaceDomain('');
        setNewWorkspaceCode('');
        fetchEnterprises();
      } else {
        alert(data.error || 'Échec de la création du workspace');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la création du workspace');
    }
  };

  const updateUserFilter = <K extends keyof UserFilters>(key: K, value: UserFilters[K]) => {
    setUserFilters((prev) => ({ ...prev, [key]: value }));
    setUserPage(1);
  };

  const filteredUsers = useMemo(() => {
    const emailQuery = userFilters.email.trim().toLowerCase();
    return users.filter((user) => {
      if (emailQuery && !user.email.toLowerCase().includes(emailQuery)) return false;
      const accountType = user.account_type ?? 'inscription';
      if (userFilters.accountType && accountType !== userFilters.accountType) return false;
      if (userFilters.workspaceId === NO_WORKSPACE_FILTER) {
        if (user.enterprise_id) return false;
      } else if (userFilters.workspaceId && user.enterprise_id !== userFilters.workspaceId) {
        return false;
      }
      if (userFilters.role && user.role !== userFilters.role) return false;
      if (userFilters.status === 'active' && !user.active) return false;
      if (userFilters.status === 'pending' && user.active) return false;
      return true;
    });
  }, [users, userFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PAGE_SIZE));
  const safePage = Math.min(userPage, totalPages);
  const paginatedUsers = filteredUsers.slice(
    (safePage - 1) * USERS_PAGE_SIZE,
    safePage * USERS_PAGE_SIZE
  );
  const rangeStart = filteredUsers.length === 0 ? 0 : (safePage - 1) * USERS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * USERS_PAGE_SIZE, filteredUsers.length);

  if (loading) return <div>Loading...</div>;

  return (
    <div className={styles.container}>
      <header className={styles.dashboardHeader}>
        <h1>Master Dashboard DarwinX</h1>
      </header>

      <div className={styles.content}>
        <div className={styles.leftCol}>
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Créer un workspace</h2>
            </div>
            <form onSubmit={handleCreateWorkspace} className={styles.whitelistForm}>
              <input
                type="text"
                placeholder="Nom du workspace"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                className={styles.input}
                required
              />
              <input
                type="text"
                placeholder="domaine.com"
                value={newWorkspaceDomain}
                onChange={(e) => setNewWorkspaceDomain(e.target.value)}
                className={styles.input}
                required
              />
              <input
                type="text"
                placeholder="Code admin"
                value={newWorkspaceCode}
                onChange={(e) => setNewWorkspaceCode(e.target.value)}
                className={styles.input}
                required
              />
              <button type="submit" className={styles.addBtn}>Créer</button>
            </form>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Workspaces</h2>
            </div>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Domaine</th>
                    <th>Code</th>
                    <th>Users</th>
                    <th>Limite</th>
                    <th>Classement</th>
                  </tr>
                </thead>
                <tbody>
                  {enterprises.map((enterprise) => (
                    <tr key={enterprise.id}>
                      <td>{enterprise.name}</td>
                      <td>{enterprise.domain}</td>
                      <td><code>{enterprise.admin_code}</code></td>
                      <td>{formatUserLimitLabel(enterprise)}</td>
                      <td>
                        <div className={styles.whitelistForm}>
                          <input
                            type="number"
                            min={1}
                            placeholder="∞"
                            value={userLimitDrafts[enterprise.id] ?? ''}
                            onChange={(e) =>
                              setUserLimitDrafts((prev) => ({
                                ...prev,
                                [enterprise.id]: e.target.value,
                              }))
                            }
                            className={styles.input}
                          />
                          <button
                            type="button"
                            className={styles.addBtn}
                            disabled={savingUserLimitId === enterprise.id}
                            onClick={() => handleSaveUserLimit(enterprise.id)}
                          >
                            {savingUserLimitId === enterprise.id ? '…' : 'OK'}
                          </button>
                        </div>
                      </td>
                      <td>
                        <label className={styles.leaderboardToggle}>
                          <input
                            type="checkbox"
                            checked={isLeaderboardOn(enterprise)}
                            disabled={savingLeaderboardId === enterprise.id}
                            onChange={(e) => handleToggleLeaderboard(enterprise.id, e.target.checked)}
                          />
                          <span>{isLeaderboardOn(enterprise) ? 'Activé' : 'Désactivé'}</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={styles.instruction}>
              Créez de nouvelles histoires dans le Scenario Studio, ou liez ici un dossier déjà présent sur le disque / Spaces.
            </p>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Tags stats</h2>
            </div>
            <p className={styles.instruction}>
              Personnalisez les noms des rôles affichés dans les statistiques et le formulaire de choix. Les identifiants internes restent inchangés.
            </p>
            <select
              value={labelsWorkspaceId}
              onChange={(e) => setLabelsWorkspaceId(e.target.value)}
              className={`${styles.input} ${styles.labelsWorkspaceSelect}`}
            >
              <option value="">Sélectionner un workspace</option>
              {enterprises.map((enterprise) => (
                <option key={enterprise.id} value={enterprise.id}>{enterprise.name}</option>
              ))}
            </select>
            {labelsWorkspaceId ? (
              <div className={styles.labelsEditorWrap}>
                <DepartmentLabelsEditor enterpriseId={labelsWorkspaceId} />
              </div>
            ) : (
              <p className={styles.instruction}>Choisissez un workspace pour modifier les noms de ses tags.</p>
            )}
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Hôtes SSO (sous-domaines)</h2>
            </div>
            <p className={styles.instruction}>
              Associez un hôte public (ex. <code>culture-gouv.cybernovel.net</code> ou le slug{' '}
              <code>culture-gouv</code>) au workspace rattaché automatiquement lors d&apos;une connexion SSO.
            </p>
            <form onSubmit={handleAddEnterpriseHost} className={styles.whitelistForm}>
              <input
                type="text"
                placeholder="culture-gouv.cybernovel.net"
                value={newHostValue}
                onChange={(e) => setNewHostValue(e.target.value)}
                className={styles.input}
                required
              />
              <select
                value={newHostEnterpriseId}
                onChange={(e) => setNewHostEnterpriseId(e.target.value)}
                className={styles.input}
                required
              >
                <option value="">Workspace</option>
                {enterprises.map((enterprise) => (
                  <option key={enterprise.id} value={enterprise.id}>
                    {enterprise.name}
                  </option>
                ))}
              </select>
              <button type="submit" className={styles.addBtn}>Ajouter</button>
            </form>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Hôte</th>
                    <th>Workspace</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {enterpriseHosts.length === 0 ? (
                    <tr>
                      <td colSpan={3}>Aucun mapping configuré</td>
                    </tr>
                  ) : (
                    enterpriseHosts.map((entry) => (
                      <tr key={entry.host}>
                        <td><code>{entry.host}</code></td>
                        <td>{entry.enterprise_name || entry.enterprise_id}</td>
                        <td>
                          <button
                            type="button"
                            className={styles.deleteBtn}
                            onClick={() => handleDeleteEnterpriseHost(entry.host)}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>IdP OIDC par workspace</h2>
            </div>
            <p className={styles.instruction}>
              Un fournisseur d&apos;identité par workspace. Mappez d&apos;abord l&apos;hôte
              (ex. <code>culture-gouv.cybernovel.net</code>), puis saisissez issuer, client id et secret.
              Le bouton SSO n&apos;apparaît que sur les sous-domaines ainsi configurés.
            </p>
            <select
              value={oidcWorkspaceId}
              onChange={(e) => setOidcWorkspaceId(e.target.value)}
              className={styles.input}
            >
              <option value="">Sélectionner un workspace</option>
              {enterprises.map((enterprise) => (
                <option key={enterprise.id} value={enterprise.id}>
                  {enterprise.name}
                </option>
              ))}
            </select>
            {oidcWorkspaceId ? (
              <form onSubmit={handleSaveOidc} className={styles.oidcForm}>
                <input
                  type="url"
                  placeholder="Issuer (https://sso.exemple.fr/realms/xxx)"
                  value={oidcIssuer}
                  onChange={(e) => setOidcIssuer(e.target.value)}
                  className={styles.input}
                  required
                />
                <input
                  type="text"
                  placeholder="Client ID"
                  value={oidcClientId}
                  onChange={(e) => setOidcClientId(e.target.value)}
                  className={styles.input}
                  required
                />
                <input
                  type="password"
                  placeholder={oidcHasSecret ? 'Secret (laisser vide pour ne pas changer)' : 'Client secret'}
                  value={oidcSecret}
                  onChange={(e) => setOidcSecret(e.target.value)}
                  className={styles.input}
                  required={!oidcHasSecret}
                />
                <input
                  type="text"
                  placeholder="Scopes"
                  value={oidcScope}
                  onChange={(e) => setOidcScope(e.target.value)}
                  className={styles.input}
                />
                <label className={styles.oidcEnabled}>
                  <input
                    type="checkbox"
                    checked={oidcEnabled}
                    onChange={(e) => setOidcEnabled(e.target.checked)}
                  />
                  SSO activé
                </label>
                {oidcRedirectUris.length > 0 && (
                  <p className={styles.instruction}>
                    Redirect URI à déclarer chez l&apos;IdP :{' '}
                    {oidcRedirectUris.map((uri) => (
                      <code key={uri}>{uri} </code>
                    ))}
                  </p>
                )}
                <div className={styles.whitelistForm}>
                  <button type="submit" className={styles.addBtn} disabled={savingOidc}>
                    {savingOidc ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  {oidcConfigured && (
                    <button type="button" className={styles.deleteBtn} onClick={handleDeleteOidc}>
                      Supprimer
                    </button>
                  )}
                </div>
              </form>
            ) : (
              <p className={styles.instruction}>Choisissez un workspace pour configurer son IdP.</p>
            )}
          </section>

          <ScormPackagesPanel enterprises={enterprises} scenarios={storageScenarios} />

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Histoires du disque</h2>
            </div>
            <p className={styles.instruction}>
              Liez un dossier scénario existant à un workspace sans copier les fichiers. Une histoire ne peut être liée qu&apos;à un seul workspace.
            </p>
            <div className={styles.whitelistForm}>
              <select
                value={selectedStoryWorkspaceId}
                onChange={(e) => setSelectedStoryWorkspaceId(e.target.value)}
                className={styles.input}
              >
                <option value="">Sélectionner un workspace</option>
                {enterprises.map((enterprise) => (
                  <option key={enterprise.id} value={enterprise.id}>
                    {enterprise.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedStoryWorkspaceId ? (
              <>
                <form onSubmit={handleLinkStory} className={styles.whitelistForm}>
                  <select
                    value={selectedOrphanScenarioId}
                    onChange={(e) => setSelectedOrphanScenarioId(e.target.value)}
                    className={styles.input}
                    disabled={orphanScenarios.length === 0 || linkingStory}
                  >
                    <option value="">
                      {orphanScenarios.length === 0
                        ? 'Aucune histoire orpheline'
                        : 'Choisir une histoire orpheline'}
                    </option>
                    {orphanScenarios.map((scenario) => (
                      <option key={scenario.id} value={scenario.id}>
                        {formatStorageScenarioLabel(scenario)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    className={styles.addBtn}
                    disabled={!selectedOrphanScenarioId || linkingStory}
                  >
                    {linkingStory ? 'Ajout…' : 'Ajouter'}
                  </button>
                </form>

                <div className={styles.storyGridCompact}>
                  {linkedScenariosForWorkspace.length === 0 ? (
                    <p className={styles.instruction}>Aucune histoire liée à ce workspace.</p>
                  ) : (
                    linkedScenariosForWorkspace.map((scenario) => (
                      <div key={scenario.id} className={styles.storyCardSmall}>
                        <div className={styles.storyInfo}>
                          <span className={styles.storyTitle}>
                            {scenario.version ? `${scenario.title} (${scenario.version})` : scenario.title}
                          </span>
                          <span className={styles.storyId}>{scenario.id}</span>
                        </div>
                        <button
                          type="button"
                          className={styles.actionBtn}
                          disabled={unlinkingScenarioId === scenario.id}
                          onClick={() => handleUnlinkStory(scenario.id, scenario.title)}
                        >
                          {unlinkingScenarioId === scenario.id ? '…' : 'Retirer'}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <p className={styles.instruction}>Choisissez un workspace pour gérer ses histoires.</p>
            )}
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Whitelist emails (auto-activation)</h2>
            </div>
            <p className={styles.instruction}>
              Les utilisateurs qui s&apos;inscrivent avec un de ces emails sont activés automatiquement.
            </p>
            <form onSubmit={handleAddWhitelistEmail} className={styles.whitelistForm}>
              <input
                type="email"
                placeholder="email@exemple.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className={styles.input}
              />
              <button type="submit" className={styles.addBtn}>Ajouter</button>
            </form>
            <div className={styles.domainList}>
              {whitelistedEmails.map((entry) => (
                <div key={entry.email} className={styles.domainItem}>
                  <span>{entry.email}</span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleRemoveWhitelistEmail(entry.email)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className={styles.rightCol}>
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>Utilisateurs</h2>
            </div>
            <p className={styles.instruction}>
              Chaque utilisateur appartient à un seul workspace. Les scénarios et analytics suivent ce rattachement.
            </p>
            <div className={styles.usersTableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Workspace</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                  <tr className={styles.filterRow}>
                    <th>
                      <input
                        type="text"
                        placeholder="Filtrer…"
                        value={userFilters.email}
                        onChange={(e) => updateUserFilter('email', e.target.value)}
                        className={styles.filterInput}
                      />
                    </th>
                    <th>
                      <select
                        value={userFilters.accountType}
                        onChange={(e) => updateUserFilter('accountType', e.target.value as UserFilters['accountType'])}
                        className={styles.filterInput}
                      >
                        <option value="">Tous</option>
                        <option value="sso">SSO</option>
                        <option value="lms">LMS (SCORM)</option>
                        <option value="inscription">Inscription</option>
                      </select>
                    </th>
                    <th>
                      <select
                        value={userFilters.workspaceId}
                        onChange={(e) => updateUserFilter('workspaceId', e.target.value)}
                        className={styles.filterInput}
                      >
                        <option value="">Tous</option>
                        <option value={NO_WORKSPACE_FILTER}>Sans workspace</option>
                        {enterprises.map((enterprise) => (
                          <option key={enterprise.id} value={enterprise.id}>
                            {enterprise.name}
                          </option>
                        ))}
                      </select>
                    </th>
                    <th>
                      <select
                        value={userFilters.role}
                        onChange={(e) => updateUserFilter('role', e.target.value)}
                        className={styles.filterInput}
                      >
                        <option value="">Tous</option>
                        {USER_ROLES.map((role) => (
                          <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                        ))}
                      </select>
                    </th>
                    <th>
                      <select
                        value={userFilters.status}
                        onChange={(e) => updateUserFilter('status', e.target.value as UserFilters['status'])}
                        className={styles.filterInput}
                      >
                        <option value="">Tous</option>
                        <option value="active">Actif</option>
                        <option value="pending">En attente</option>
                      </select>
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={styles.emptyRow}>
                        Aucun utilisateur pour ces filtres.
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.email}</td>
                        <td>{ACCOUNT_TYPE_LABELS[user.account_type ?? 'inscription']}</td>
                        <td>
                          <select
                            value={user.enterprise_id ?? ''}
                            disabled={savingWorkspaceUserId === user.id}
                            onChange={(e) => handleWorkspaceChange(user.id, e.target.value)}
                            className={styles.input}
                          >
                            <option value="">—</option>
                            {enterprises.map((enterprise) => (
                              <option key={enterprise.id} value={enterprise.id}>
                                {enterprise.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            value={user.role}
                            disabled={savingRoleUserId === user.id}
                            onChange={(e) => handleRoleChange(user.id, e.target.value as UserRole)}
                            className={styles.input}
                          >
                            {USER_ROLES.map((role) => (
                              <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                            ))}
                          </select>
                        </td>
                        <td>{user.active ? 'Actif' : 'En attente'}</td>
                        <td>
                          {user.active ? (
                            <button
                              type="button"
                              className={styles.actionBtn}
                              onClick={() => handleDeactivateUser(user.id)}
                            >
                              Désactiver
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={styles.actionBtn}
                              onClick={() => handleActivateUser(user.id)}
                            >
                              Activer
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.pagination}>
              <span>
                {filteredUsers.length === 0
                  ? '0 utilisateur'
                  : `${rangeStart}–${rangeEnd} sur ${filteredUsers.length}`}
              </span>
              <div className={styles.paginationBtns}>
                <button
                  type="button"
                  className={styles.actionBtn}
                  disabled={safePage <= 1}
                  onClick={() => setUserPage((page) => Math.max(1, page - 1))}
                >
                  Précédent
                </button>
                <span className={styles.pageIndicator}>
                  {safePage} / {totalPages}
                </span>
                <button
                  type="button"
                  className={styles.actionBtn}
                  disabled={safePage >= totalPages}
                  onClick={() => setUserPage((page) => Math.min(totalPages, page + 1))}
                >
                  Suivant
                </button>
              </div>
            </div>

            <div className={styles.rolesGuide}>
              <h3 className={styles.rolesGuideTitle}>Les rôles</h3>
              <ul className={styles.rolesList}>
                {USER_ROLES.map((role) => (
                  <li key={role} className={styles.roleItem}>
                    <span className={styles.roleName}>{ROLE_LABELS[role]}</span>
                    <span className={styles.roleDescription}>{ROLE_DESCRIPTIONS[role]}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DarwinXDashboard;
